import React, { useState, useEffect } from "react";
import TtsMinimalPlayer from "./tts-minimal-player";

interface GlobalTtsPlayerState {
  isVisible: boolean;
  audioUrl: string | null;
  isPlaying: boolean;
  playbackRate: number;
  audioElement: HTMLAudioElement | null;
}

const GlobalTtsPlayer: React.FC = () => {
  const [playerState, setPlayerState] = useState<GlobalTtsPlayerState>({
    isVisible: false,
    audioUrl: null,
    isPlaying: false,
    playbackRate: 1,
    audioElement: null,
  });

  useEffect(() => {
    const handleTtsPlay = (event: CustomEvent) => {
      const { audioUrl, isPlaying, audioElement } = event.detail;
      setPlayerState((prev) => ({
        ...prev,
        isVisible: true,
        audioUrl,
        isPlaying,
        audioElement,
      }));
    };

    const handleTtsPause = () => {
      setPlayerState((prev) => ({
        ...prev,
        isPlaying: false,
      }));
    };

    const handleTtsStop = () => {
      setPlayerState((prev) => ({
        ...prev,
        isVisible: false,
        isPlaying: false,
        audioUrl: null,
        audioElement: null,
      }));
    };

    window.addEventListener("tts-play", handleTtsPlay as EventListener);
    window.addEventListener("tts-pause", handleTtsPause as EventListener);
    window.addEventListener("tts-stop", handleTtsStop as EventListener);

    return () => {
      window.removeEventListener("tts-play", handleTtsPlay as EventListener);
      window.removeEventListener("tts-pause", handleTtsPause as EventListener);
      window.removeEventListener("tts-stop", handleTtsStop as EventListener);
    };
  }, []);

  const handlePlayPause = () => {
    if (!playerState.audioElement) return;

    const newIsPlaying = !playerState.isPlaying;

    if (newIsPlaying) {
      playerState.audioElement.play();
    } else {
      playerState.audioElement.pause();
    }

    setPlayerState((prev) => ({
      ...prev,
      isPlaying: newIsPlaying,
    }));
  };

  useEffect(() => {
    const audioElement = playerState.audioElement;
    if (!audioElement) return;

    const handleEnded = () => {
      setPlayerState((prev) => ({
        ...prev,
        isVisible: false,
        isPlaying: false,
      }));
    };

    const handleCanPlay = () => {
    };

    const handlePlaying = () => {
    };

    audioElement.addEventListener("ended", handleEnded);
    audioElement.addEventListener("canplay", handleCanPlay);
    audioElement.addEventListener("playing", handlePlaying);

    return () => {
      audioElement.removeEventListener("ended", handleEnded);
      audioElement.removeEventListener("canplay", handleCanPlay);
      audioElement.removeEventListener("playing", handlePlaying);
    };
  }, [playerState.audioElement]);

  const handleClose = () => {
    if (playerState.audioElement) {
      playerState.audioElement.pause();
    }

    setPlayerState((prev) => ({
      ...prev,
      isVisible: false,
      isPlaying: false,
      audioUrl: null,
      audioElement: null,
    }));

    // Same full stop as the speaker button — clears TTS session so next click starts fresh.
    window.dispatchEvent(new CustomEvent("tts-player-closed"));
  };

  const handleSpeedChange = (speed: number) => {
    if (playerState.audioElement) {
      playerState.audioElement.playbackRate = speed;
    }

    setPlayerState((prev) => ({
      ...prev,
      playbackRate: speed,
    }));
  };

  if (!playerState.isVisible || !playerState.audioUrl) {
    return null;
  }

  return (
    <TtsMinimalPlayer
      audioUrl={playerState.audioUrl}
      isPlaying={playerState.isPlaying}
      onPlayPause={handlePlayPause}
      onClose={handleClose}
      onSpeedChange={handleSpeedChange}
    />
  );
};

export default GlobalTtsPlayer;
